import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withStyles, Stepper, Step, StepConnector, StepButton, Grid } from "@material-ui/core";
import PropTypes from "prop-types";
import { quoteSteps } from "../constants";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { quoteStepperConnectorStyles } from "features/quote/styles";
import QuoteHeaderSummary from "./QuoteHeaderSummary";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { QUOTE_QUERY } from "features/quotes/queries";
import AssetsWarning from "features/assets/components/AssetsWarning";
import { set, useStateLens } from "@totalsoft/react-state-lens";
import { emptyObject } from "constants/defaultValues";
import { quoteStatus } from "constants/quoteStatus";

const QuoteStepConnector = withStyles(quoteStepperConnectorStyles)(StepConnector);

function QuoteStepper({ quoteId, activeStepId, nextStepId, disabledNextStep, onHandleNextStep }) {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const openDialogLens = useStateLens(false);
  const quoteExists = Boolean(quoteId);
  const [stepperOrientation, setStepperOrientation] = useState("horizontal");

  const { data, loading: loadingQuote } = useQueryWithErrorHandling(QUOTE_QUERY, {
    variables: { quoteId },
    skip: !quoteExists,
  });
  const quote = data?.quote || emptyObject;
  const isEstimation = location.pathname === "/estimations/new/assets" || quote?.isEstimation;

  const steps = useMemo(
    () => [
      {
        id: quoteSteps.client,
        label: t("Quote.ClientStep"),
        path: "client",
        disabled: (_activeStepId, _nextStepId, _disabledNextStep) => {
          return (
            _activeStepId === quoteSteps.client ||
            _activeStepId === quoteSteps.dataCompletion ||
            _activeStepId === quoteSteps.contract ||
            quote?.quoteStatusId !== quoteStatus.noFinanceProduct.id
          );
        },
        completed: (activeStepId) => {
          return activeStepId > quoteSteps.client;
        },
        visible: (quote) => {
          return !quote.isEstimation || activeStepId === quoteSteps.client;
        },
      },
      {
        id: quoteSteps.assets,
        label: t("Quote.AssetsStep"),
        path: "assets",
        disabled: (_activeStepId, _nextStepId, _disabledNextStep) => {
          return (
            _activeStepId === quoteSteps.assets ||
            quoteSteps.assets - _activeStepId > 1 ||
            (_nextStepId === quoteSteps.assets && _disabledNextStep)
          );
        },
        completed: (activeStepId) => {
          return activeStepId > quoteSteps.assets;
        },
        visible: () => {
          return true;
        },
      },
      {
        id: quoteSteps.financeProduct,
        label: t("Quote.ProductStep"),
        path: "products",
        disabled: (_activeStepId, _nextStepId, _disabledNextStep) => {
          return (
            _activeStepId === quoteSteps.financeProduct ||
            quoteSteps.financeProduct - _activeStepId > 1 ||
            (_nextStepId === quoteSteps.financeProduct && _disabledNextStep)
          );
        },
        completed: (activeStepId) => {
          return activeStepId > quoteSteps.financeProduct;
        },
        visible: () => {
          return true;
        },
      },
      {
        id: quoteSteps.dataCompletion,
        label: t("Quote.DataCompletionStep"),
        path: "datacompletion",
        disabled: (_activeStepId, _nextStepId, _disabledNextStep) => {
          return (
            _activeStepId === quoteSteps.dataCompletion ||
            quoteSteps.dataCompletion - _activeStepId > 1 ||
            (_nextStepId === quoteSteps.dataCompletion && _disabledNextStep)
          );
        },
        completed: (activeStepId) => {
          return activeStepId > quoteSteps.dataCompletion;
        },
        visible: (quote) => {
          return !quote.isEstimation || activeStepId === quoteSteps.client;
        },
      },
      {
        id: quoteSteps.contract,
        label: t("Quote.ContractStep"),
        path: "contract",
        disabled: (_activeStepId, _nextStepId, _disabledNextStep) => {
          return (
            _activeStepId === quoteSteps.contract ||
            quoteSteps.contract - _activeStepId > 1 ||
            (_nextStepId === quoteSteps.contract && _disabledNextStep)
          );
        },
        completed: (activeStepId) => {
          return activeStepId > quoteSteps.contract;
        },
        visible: (quote) => {
          return !quote.isEstimation || activeStepId === quoteSteps.client;
        },
      },
    ],
    [t, activeStepId, quote]
  );
  const visibleSteps = useMemo(() => steps.filter((x) => !loadingQuote && x.visible({ ...quote, isEstimation })), [
    isEstimation,
    steps,
    quote,
    loadingQuote,
  ]);

  const handleStep = useCallback(
    (step) => async () => {
      if (activeStepId > quoteSteps.client && step.id === quoteSteps.client) {
        if (!quoteId) history.push({ pathname: `/quotes/new/${step.path}` });
        if (quoteId) {
          if (quote?.assetNo > 0) (openDialogLens |> set)(true);
          else history.push({ pathname: `/quotes/${quoteId}/${step.path}` });
        }
      } else if (step.id > activeStepId) onHandleNextStep();
      else history.push({ pathname: `/quotes/${quoteId || "new"}/${step.path}` });
    },
    [history, quoteId, activeStepId, openDialogLens, onHandleNextStep, quote?.assetNo]
  );

  const handleResize = useCallback(() => {
    if (window.innerWidth < 600) setStepperOrientation("vertical");
    else setStepperOrientation("horizontal");
  }, []);

  useEffect(() => {
    if (window.innerWidth < 600) setStepperOrientation("vertical");
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Stepper
            activeStep={visibleSteps.findIndex((x) => x.id === activeStepId)}
            orientation={stepperOrientation}
            connector={<QuoteStepConnector />}
          >
            {steps
              .filter((x) => !loadingQuote && x.visible({ ...quote, isEstimation }))
              .map((step) => (
                <Step key={step.id} disabled={step.disabled(activeStepId, nextStepId, disabledNextStep)}>
                  <StepButton
                    onClick={handleStep(step)}
                    completed={step.completed(activeStepId)}
                    disabled={step.disabled(activeStepId, nextStepId, disabledNextStep)}
                  >
                    {step.label}
                  </StepButton>
                </Step>
              ))}
          </Stepper>
        </Grid>
        <Grid item xs={12}>
          <QuoteHeaderSummary quoteId={quoteId} activeStepId={activeStepId} />
        </Grid>
      </Grid>
      <AssetsWarning openDialogLens={openDialogLens} quoteId={quoteId} />
    </>
  );
}

QuoteStepper.propTypes = {
  quoteId: PropTypes.number,
  activeStepId: PropTypes.number.isRequired,
  nextStepId: PropTypes.number.isRequired,
  disabledNextStep: PropTypes.bool.isRequired,
  onHandleNextStep: PropTypes.func.isRequired,
};

export default QuoteStepper;
